import { type LucideIcon, Building2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  SearchDropdown,
  SearchDropdownItem,
} from '@/components/ui/search-dropdown'
import { useSidebar } from '@/components/ui/sidebar'

type Company = {
  id?: string
  name: string
  logo: LucideIcon | string
  plan: string
  subtitle?: string
}

type CompanySwitcherProps = {
  companies?: Company[]
  onCompanySelect?: (company: Company) => void | Promise<void>
  fetchCompanies?: (query?: string) => Promise<Company[]>
  loadInitialCompanies?: boolean
  placeholder?: string
  selectedCompany?: Company | null
  showLogoInButton?: boolean
}

export function CompanySwitcher({
  companies = [],
  selectedCompany: initialSelectedCompany,
  onCompanySelect,
  fetchCompanies,
  loadInitialCompanies = true,
  placeholder = 'Search companies...',
  showLogoInButton = true,
}: CompanySwitcherProps) {
  const { state, isMobile } = useSidebar()
  const isCollapsed = state === 'collapsed'

  // Use the selectedCompany from props as the single source of truth
  const selectedCompany = initialSelectedCompany || companies[0] || null

  const handleCompanySelect = async (company: Company) => {
    await onCompanySelect?.(company)
  }

  const fetchItems = async (query?: string) => {
    if (fetchCompanies) {
      try {
        const results = await fetchCompanies(query)
        return results.map(company => ({
          id: company.id || company.name,
          label: company.name,
          sublabel: company.subtitle,
          data: company,
        }))
      } catch (error) {
        console.error('Failed to fetch companies:', error)
        return []
      }
    }

    // Filter static companies
    const filtered = query
      ? companies.filter(company =>
          company.name.toLowerCase().includes(query.toLowerCase()),
        )
      : companies

    return filtered.map(company => ({
      id: company.id || company.name,
      label: company.name,
      sublabel: company.subtitle,
      data: company,
    }))
  }

  const renderLogo = (company: Company) => {
    if (typeof company.logo === 'string') {
      return (
        <img
          src={company.logo}
          alt={company.name}
          className="h-6 w-6 object-contain"
        />
      )
    }
    const Logo = company.logo
    return <Logo className="h-4 w-4" />
  }

  const selectedItem = selectedCompany
    ? {
        id: selectedCompany.id || selectedCompany.name,
        label: selectedCompany.name,
        sublabel: selectedCompany.subtitle,
        data: selectedCompany,
      }
    : undefined

  return (
    <div className="z-100 relative absolute top-0 left-0 w-full">
      <SearchDropdown<Company>
        fetchItems={fetchItems}
        selectedItems={selectedItem ? [selectedItem] : []}
        onSelect={item => {
          const companyItem = item as SearchDropdownItem<Company> | null
          if (companyItem?.data) {
            handleCompanySelect(companyItem.data)
          }
        }}
        multiSelect={false}
        placeholder="Select a company"
        searchPlaceholder={placeholder}
        buttonLabel={
          !isCollapsed ? selectedCompany?.name || 'Select company' : undefined
        }
        icon={
          selectedCompany && showLogoInButton ? (
            <div className="flex h-6 w-6 items-center justify-center rounded-xl border bg-muted text-sm font-medium overflow-hidden shrink-0">
              {renderLogo(selectedCompany)}
            </div>
          ) : undefined
        }
        buttonClassName={cn(
          'w-full',
          isCollapsed && 'w-8 h-8 p-0 justify-center',
        )}
        popoverSide={isMobile || isCollapsed ? 'right' : 'bottom'}
        popoverAlign={isCollapsed ? 'start' : 'start'}
        popoverClassName="w-64"
        loadInitialData={loadInitialCompanies}
        emptyMessage="No companies found"
        loadingMessage="Searching companies..."
        renderItem={item => (
          <>
            <div className="flex h-6 w-6 items-center justify-center rounded-lg border bg-muted text-sm font-medium overflow-hidden">
              {item.data ? (
                renderLogo(item.data)
              ) : (
                <Building2 className="h-4 w-4" />
              )}
            </div>
            <div className="flex-1">
              <div className="font-medium">{item.label}</div>
              {item.sublabel && (
                <div className="text-xs text-muted-foreground">
                  {item.sublabel}
                </div>
              )}
            </div>
          </>
        )}
        showChevron={!isCollapsed}
      />
    </div>
  )
}
